import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Distance as DistanceType } from '@/types/Distance';
import { FacetOption as FacetOptionType } from '@/types/FacetOption';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getInitialWidth } from '@/redux/modules/windowSize';
import { MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import { useAppSelector } from '@/redux/hooks';
import AutoCheckbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/AutoCheckbox';
import CheckFacet from '../../CheckboxFacet';
import Facet from '../../Facet';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import RadioFacet from '@/components/Facets/RadioFacet';
import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import ScrollBar from '@/components/ScrollBar/ScrollBar';
import SeeMoreItems from '@/components/SeeMoreItems/SeeMoreItems';
import SelectedCountry from '../../SelectedCountry';
import styled, { css, useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

const DISTANCE_COUNTRIES = ['', 'US'];
const DISTANCE_OPTIONS = [20, 40, 60, 80, 100, 120, 250, 500, 1000];
const distanceOptions = DISTANCE_OPTIONS.map((option) => {
    return { label: option, value: option };
});
const MAX_HEIGHT_FOR_CHECKS = 400;
const MULTISELECT_FACET_LIMIT = 4;

type Props = {
    availableCountries: FacetOptionType[];
    countries: FacetOptionType[];
    distance: DistanceType;
    distanceSelected: (countryName: string, zipCode: string, range: number, measurement: string) => void;
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    loadPageWithFacetCollapsed: boolean;
    removeCountryAndDistance: (event?: any) => void;
    replaceCountry: (countryId: string) => void;
    shippingFacet: { options?: FacetOptionType[] };
    title: string;
};

const LocationShippingFacet = ({
    availableCountries,
    countries,
    distance,
    distanceSelected,
    facetOptionChecked,
    loadPageWithFacetCollapsed,
    removeCountryAndDistance,
    replaceCountry,
    shippingFacet,
    title,
}: Props): ReactElement<Props> => {
    const {
        breakpoints: { mobileWidthNum },
    } = useTheme();
    const ssrWidth = useAppSelector(getInitialWidth);
    const isNarrowerThanMobile = useIsNarrowerThan(mobileWidthNum + 1, ssrWidth);

    const intl = useIntl();
    const [changeCountry, setChangeCountry] = useState(false);
    const [range, setRange] = useState(20);
    const [showScrollBar, setShowScrollBar] = useState(false);
    const [withinChecked, setWithinChecked] = useState(false);
    const [zipCode, setZipCode] = useState('');
    const [zipCodeValid, setZipCodeValid] = useState(true);

    useEffect(() => {
        if (distance) {
            updateDistanceState(distance);
        }
    }, [distance]);

    const updateDistanceState = (distance: DistanceType) => {
        if (distance?.zipCode) {
            setRange(distance.space);
            setShowScrollBar(false);
            setWithinChecked(Boolean(distance.zipCode));
            setZipCode(distance.zipCode);
            setZipCodeValid(true);
        } else {
            setRange(20);
            setShowScrollBar(false);
            setWithinChecked(false);
            setZipCode('');
            setZipCodeValid(true);
        }
    };

    const onCountryRevealClick = () => {
        setChangeCountry(!changeCountry);
    };

    const onSeeAllCountriesClick = () => {
        setShowScrollBar(!showScrollBar);
    };

    const anywhereCheck = (event: ChangeEvent<HTMLInputElement>) => {
        removeCountryAndDistance();
        event.currentTarget.name = '';
        setChangeCountry(false);
        setShowScrollBar(false);
    };

    const onChangeCountry = (event: ChangeEvent<HTMLInputElement>) => {
        const countryId = event.currentTarget.name;
        if (!countryId.length) {
            anywhereCheck(event);
        } else {
            replaceCountry(countryId);
            setChangeCountry(false);
            setShowScrollBar(false);
        }
    };

    const checkForShipping = (id: string, event: React.MouseEvent<HTMLInputElement>) => {
        facetOptionChecked('shipping', id, event.currentTarget.checked);
    };

    const onDistanceSelected = (event: any) => {
        setRange(Number(event.value));
    };

    const zipChanged = (event: any) => {
        const zip = event.target.value;
        if (zip.length <= 5 && (!isNaN(zip) || zip === '')) {
            setWithinChecked(true);
            setZipCode(zip);
        }
    };

    const distanceClickHandler = () => {
        const country = 'US';
        const measurement = 'mile';
        const isValid = zipCode.length === 5;
        setZipCodeValid(isValid);

        if (isValid) {
            setChangeCountry(false);
            setShowScrollBar(false);
            setWithinChecked(true);
            distanceSelected(country, zipCode, range, measurement);
        }
    };

    const distanceEnterKeyHandler = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            distanceClickHandler();
        }
    };

    const onCheckWithin = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.currentTarget.checked;

        setChangeCountry(false);
        setShowScrollBar(false);
        setWithinChecked(isChecked);
        if (!isChecked) {
            setZipCode('');
            setZipCodeValid(true);
            removeCountryAndDistance();
        }
    };

    const createShippingCheckbox = (option: FacetOptionType) => (
        <CheckFacet
            key={option.id}
            onChange={(event) => {
                checkForShipping(option.id, event);
            }}
            option={option}
        />
    );

    const renderSelectedCountry = (option: FacetOptionType) => {
        const linkText = <FormattedMessage id="searchChangeCountry" />;
        return (
            <SelectedCountry
                key={option.id}
                linkText={linkText}
                onChange={onCountryRevealClick}
                option={option}
            />
        );
    };

    const customCountryOrder = (countries: FacetOptionType[]) => {
        // places these countries at the head of a new list
        const filtered = ['US', 'UK', 'CA'];
        const reordered = countries.filter((c) => !filtered.includes(c.id));
        const us = countries.filter((c) => c.id === 'US')[0];
        const uk = countries.filter((c) => c.id === 'UK')[0];
        const ca = countries.filter((c) => c.id === 'CA')[0];
        ca && reordered.unshift(ca);
        uk && reordered.unshift(uk);
        us && reordered.unshift(us);
        return reordered;
    };

    const getShippingOptions = (facetOptions: FacetOptionType[], showDistance: boolean) => {
        let filteredOptions = facetOptions.slice();
        if (filteredOptions.length && showDistance) {
            filteredOptions = filteredOptions.filter((facet) => facet.id !== 'freeLocalPickup');
        }
        return filteredOptions.map(createShippingCheckbox);
    };

    const getLocalPickupOption = (facetOptions: FacetOptionType[]) => {
        const freePickupOption = facetOptions.filter((facet) => facet.id === 'freeLocalPickup');
        return freePickupOption.length && freePickupOption[0];
    };

    const selectedCountry: FacetOptionType = countries.filter((country) => country.selected)[0] || {
        id: '',
        name: 'Anywhere',
        selected: true,
    };

    let interimlist = availableCountries.map((country: FacetOptionType) => ({
        ...country,
        selected: country.id === selectedCountry.id,
    }));
    interimlist = customCountryOrder(interimlist);
    interimlist.unshift({
        id: '',
        name: 'Anywhere',
        selected: !selectedCountry.id.length,
    });
    const showDistance = DISTANCE_COUNTRIES.includes(selectedCountry.id);
    const shippingOptions = shippingFacet && getShippingOptions(shippingFacet.options, showDistance);
    const localPickupOption =
        showDistance && shippingFacet?.options.length && getLocalPickupOption(shippingFacet.options);
    const localPickupCheckbox = localPickupOption && createShippingCheckbox(localPickupOption);

    let validationFormattedMessageId;
    if (!zipCodeValid) {
        validationFormattedMessageId = 'you_must_enter_a_valid_zip_code';
    }
    const facetClass = title.trim().replace('/', '').replace(/\s+/g, '-').toLowerCase();

    return (
        <Facet
            className={`distanceFacet ${facetClass}`}
            closeFacet={loadPageWithFacetCollapsed}
            title={title}
        >
            <ScrollBarStyles
                maxHeight={MAX_HEIGHT_FOR_CHECKS}
                showScrollBar={showScrollBar}
            >
                <StyledCountriesList>
                    {changeCountry ? (
                        <StyledSeeMore
                            itemsComponentFull={
                                <RadioFacet
                                    onChange={onChangeCountry}
                                    options={interimlist}
                                />
                            }
                            itemsComponentShort={
                                <RadioFacet
                                    onChange={onChangeCountry}
                                    options={interimlist.slice(0, MULTISELECT_FACET_LIMIT)}
                                />
                            }
                            itemsListLengthFull={interimlist.length}
                            itemsListLengthShort={interimlist.slice(0, MULTISELECT_FACET_LIMIT).length}
                            onSeeMoreItemsClick={onSeeAllCountriesClick}
                            seeLessFormattedMessageId="search_distance_view_less_countries"
                            seeMoreFormattedMessageId="search_distance_view_all_countries"
                            showTotal
                        />
                    ) : (
                        renderSelectedCountry(selectedCountry)
                    )}
                </StyledCountriesList>
                {showDistance && (
                    <div>
                        <SearchField $addMargin={zipCodeValid}>
                            <Input
                                aria-label={intl.formatMessage({
                                    id: 'search_distance_zip_code',
                                })}
                                errorMessage={<FormattedMessage id={validationFormattedMessageId} />}
                                hideSpacer={zipCodeValid}
                                id="zipcode"
                                onChange={zipChanged}
                                onKeyPress={distanceEnterKeyHandler}
                                placeholder={intl.formatMessage({
                                    id: 'search_distance_zip_code',
                                })}
                                testid="zipCodeInput"
                                valid={zipCodeValid}
                                value={zipCode}
                            />
                            <StyledIconWrapper $hideSpace={zipCodeValid}>
                                <MagnifyingGlass
                                    color="inverse"
                                    data-testid="zip-button"
                                    onClick={distanceClickHandler}
                                    size="sm"
                                />
                            </StyledIconWrapper>
                        </SearchField>
                        <StyledWithInControls>
                            <StyledToggleOption>
                                <StyledAutoCheckboxToggler
                                    label={intl.formatMessage({ id: 'within' })}
                                    labelDisplay={
                                        <BodySecondary className="transform-none">
                                            <FormattedMessage id="within" />
                                        </BodySecondary>
                                    }
                                    name="distance"
                                    // @ts-expect-error Checkboxes should have an onChange of type React.ChangeEvent<HTMLInputElement>
                                    // but AutoCheckbox uses React.FormEvent
                                    onChange={onCheckWithin}
                                    startChecked={withinChecked}
                                />
                            </StyledToggleOption>
                            <StyledSelect
                                isSearchable={!isNarrowerThanMobile}
                                name="sort"
                                onChange={onDistanceSelected}
                                options={distanceOptions}
                                small
                                usePortalMenu
                                value={distanceOptions.find((op) => op.value === range)}
                            />
                            <StyledLabel>mi</StyledLabel>
                        </StyledWithInControls>
                        {Boolean(localPickupCheckbox) && localPickupCheckbox}
                    </div>
                )}
                <div>{shippingOptions}</div>
            </ScrollBarStyles>
        </Facet>
    );
};

export default LocationShippingFacet;

const StyledAutoCheckboxToggler = styled(AutoCheckbox)`
    margin-top: 0;

    input[type='checkbox'] {
        display: none;
    }

    label {
        margin: 0;
        padding: 0;
        text-transform: none;

        input[type='radio'] {
            position: relative;
            margin: 4px 5px 0 1px;
        }

        span {
            letter-spacing: 0.5px;
        }
    }
    &:hover {
        background-color: ${({ theme }) => theme.colors.grey400};
        color: ${({ theme }) => theme.colors.black};
    }
`;

const StyledToggleOption = styled.div`
    padding: 5px 5px 5px 0;
`;

const StyledSelect = styled<typeof Select>(Select)`
    max-width: 260px;
    min-width: 80px;
    margin-bottom: 10px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        max-width: unset;
    }
`;

const StyledLabel = styled.span`
    && {
        margin: 0 0 10px 5px;
        display: inline-block;
    }
`;

const SearchField = styled.div<{ $addMargin: boolean }>`
    display: flex;
    align-items: center;
    ${({ $addMargin, theme }) =>
        $addMargin &&
        css`
            margin-bottom: 24px;
            @media (max-width: ${theme.breakpoints.mobileWidth}) {
                margin-bottom: 16px;
            }
        `}
`;

const ScrollBarStyles = styled(ScrollBar)`
    div[class='form-group'] {
        margin: 0;
    }
`;

const StyledCountriesList = styled.div`
    margin-bottom: 15px;
`;

const StyledSeeMore = styled(SeeMoreItems).attrs(() => ({
    linkClassName: 'linkClassName',
}))`
    .linkClassName {
        cursor: pointer;
        margin-top: 4px;
        text-decoration: none;
    }
`;

const StyledIconWrapper = styled.div<{ $hideSpace: boolean }>`
    height: 42px;
    width: 42px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.blue100};
    cursor: pointer;
    transition: all 0.25s;

    ${({ $hideSpace, theme }) =>
        !$hideSpace &&
        css`
            margin-bottom: 32px;
            @media (max-width: ${theme.breakpoints.mobileWidth}) {
                margin-bottom: 24px;
            }
        `}

    &:hover {
        background-color: ${({ theme }) => theme.colors.blue200};
    }
`;

const StyledWithInControls = styled.div`
    align-items: center;
    display: flex;
`;
